<template>
  <main>
    <header>
      <main-menu
        ref="menu"
        :overlap="menuOverlap"
        :active="currentSection"
        @click="handleMenuClick"
        @scrollToTop="scrollToTop"
      />
      <header-logos :overlap="logoOverlap" @click="scrollToTop"/>
      <arrow ref="arrow" :overlap="arrowOverlap" @click="handleArrowClick"/>
    </header>
    <hero ref="hero" data-name="hero"/>
    <projects ref="projects" data-name="projects" class="section-projects"/>
    <div class="sections-wrapper" :class="{'active-secret': isActiveSecret}">
      <about ref="about" data-name="about" class="section-about"/>
      <manifest ref="manifest" data-name="manifest" class="section-manifest"/>
      <span
        class="sections-wrapper__bg"
        :style="{ backgroundImage: `url(${ manifestBackground })` }"
      />
    </div>
    <contact ref="contact" data-name="contact" class="section-contact"/>
    <cookies/>
  </main>
</template>

<script>
import throttle from 'lodash/throttle'
import smoothScroll from 'smoothscrolljs'
import { mapState, mapMutations, mapActions } from 'vuex'
import {
  isInViewport,
  findPrevious,
  findNext,
  isOverlappingVertically
} from '@/assets/js/utils'

import Hero from '@/views/Hero'
import Projects from '@/views/Projects'
import About from '@/views/About'
import Manifest from '@/views/Manifest'
import Contact from '@/views/Contact'
import MainMenu from '@/components/Menu'
import HeaderLogos from '@/components/HeaderLogos'
import Arrow from '@/components/Arrow'
import Cookies from '@/components/Cookies'

const DEFAULT_SECTION = 'hero'

export default {
  components: {
    Hero,
    Projects,
    About,
    Manifest,
    Contact,
    MainMenu,
    HeaderLogos,
    Arrow,
    Cookies
  },
  data () {
    return {
      sections: ['hero', 'projects', 'about', 'manifest', 'contact'],
      arrowOverlap: DEFAULT_SECTION,
      menuOverlap: DEFAULT_SECTION,
      logoOverlap: DEFAULT_SECTION,
      currentSection: DEFAULT_SECTION,
      setOverlapsThrottled: throttle(this.setOverlaps, 300)
    }
  },
  computed: {
    ...mapState({
      isActiveMenu: state => state.menu.isActiveMenu,
      isActiveSecret: state => state.secret.isActiveSecret,
      manifestBackground: state => state.manifest.background
    })
  },
  watch: {
    isActiveMenu () {
      document.body.classList[ this.isActiveMenu ? 'add' : 'remove' ]('active-menu')
    }
  },
  mounted () {
    this.fetchBackground()
    this.sectionElements = this.sections.map(name => this.$refs[name].$el)
    this.lastScrollPos = window.pageYOffset
    window.scrollTo(0, window.pageYOffset + 1) // Safari fix
    this.lastDirection = 'down'
    this.setOverlaps()
    window.addEventListener('scroll', this.setOverlapsThrottled.bind(this))
  },
  methods: {
    ...mapMutations(['closeMenu']),
    ...mapActions(['fetchBackground']),
    handleMenuClick (sectionName) {
      this.closeMenu()
      if (this.$refs[sectionName]) smoothScroll(this.$refs[sectionName].$el)
    },
    handleArrowClick (direction) {
      const sectionsInViewport = this.sectionElements.reduce((sections, section) => (
        isInViewport(section) ? [...sections, section] : sections
      ), [])

      if (direction === 'up') {
        const scrollTo = sectionsInViewport.length > 1
          ? sectionsInViewport[0]
          : findPrevious(sectionsInViewport[0], this.sectionElements) || 0
        smoothScroll(scrollTo)
      } else {
        const scrollTo = sectionsInViewport.length > 1
          ? sectionsInViewport[1]
          : findNext(sectionsInViewport[0], this.sectionElements) || document.body.offsetHeight
        smoothScroll(scrollTo)
      }
    },
    setOverlaps () {
      const currentScrollPos = window.pageYOffset
      const direction = currentScrollPos > this.lastScrollPos
        ? 'down'
        : currentScrollPos === this.lastScrollPos
          ? this.lastDirection
          : 'up'

      const sections = direction === 'down' ? this.sectionElements : [...this.sectionElements].reverse()
      const logoEl = document.querySelector('.logo--fixed') // TODO: Add correct ref
      sections.forEach(section => {
        const sectionProps = section.getBoundingClientRect()
        this.arrowOverlap = isOverlappingVertically(this.$refs.arrow.$el.getBoundingClientRect(), sectionProps)
          ? section.dataset.name
          : this.arrowOverlap

        this.menuOverlap = isOverlappingVertically(this.$refs.menu.$el.getBoundingClientRect(), sectionProps)
          ? section.dataset.name
          : this.menuOverlap

        this.logoOverlap = isOverlappingVertically(logoEl.getBoundingClientRect(), sectionProps)
          ? section.dataset.name
          : this.logoOverlap
      })
      this.sectionElements.forEach(section => {
        this.currentSection = ~~section.getBoundingClientRect().top <= 0
          ? section.dataset.name
          : this.currentSection
      })
      this.lastScrollPos = currentScrollPos
      this.lastDirection = direction
    },
    scrollToTop () {
      this.closeMenu()
      smoothScroll(0)
    }
  }
}
</script>

<style lang="scss">
  @import '../node_modules/normalize.css';
  @import '~@/assets/sass/fonts';

  *,
  *:after,
  *:before {
    box-sizing: border-box;
  }

  body {
    font-family: $font-default;
    font-size: $font-regular;
    overflow-x: hidden;
    line-height: 1.2;
    &.active-menu {
      overflow: hidden;
    }
    h1, h2, h3, h4 {
      font-weight: 400;
    }
  }
  .copy {
    position: relative;
    margin-top: 0;
    margin-bottom: 0;
    @include font-style-medium-bigger;
    &--heading {
      @include font-style-extreme-small;
    }
    &-cols-wrap {
      display: flex;
      @include media-down(md) {
        flex-wrap: wrap;
      }
    }
    &--lead {
      @include font-style-big-smaller;
      text-transform: uppercase;
    }
    &--right {
      text-align: right;
    }
    &-col {
      padding-right: 0!important;
      padding-left: 0!important;
    }
    &-col-3 {
      width: 247px;
      @include media-up(md) {
        width: 276px;
      }
    }
    &-col-6 {
      @include grid-column((xs: 12, md: 6));
      &-centered {
        @include grid-column((xs: 6, md: 3, lg: 6));
        line-height: 1.1;
        @include media-up(lg) {
          font-size: 34px;
          line-height: 49px;
        }
      }
      &-centered--left {
        padding-right: 5px;
        padding-left: 0;
        letter-spacing: 0.9px;
        @include grid-offset-left((xs: 0, md: 3, lg: 0));
        @include media-up(sm) {
          letter-spacing: 1px;
        }
        @include media-up(lg) {
          padding-right: 16px;
          font-family: $font-default-bold;
          letter-spacing: 5px;
        }
      }
      &-centered--right {
        padding-right: 0;
        padding-left: 5px;
        font-family: $font-default-bold;
        padding-top: 19px;
        @include media-up(lg) {
          padding-left: 16px;
          font-family: $font-default;
          padding-top: 101px;
        }
      }
    }
    &-col-8 {
      width: 100%;
      @include media-up(md) {
        width: 305px;
      }
      @include media-up(lg) {
        width: calc(100% - 276px);
      }
    }
    &-col-10 {
      @include grid-column((xs: 12, md: 5, lg: 10));
      &--right {
        @include grid-offset-left((xs: 0, md: 7, lg: 2));
      }
    }
  }
  .section-about {
    .copy {
      font-family: $font-default-bold;
    }
  }
  .sections-wrapper {
    position: relative;

    &__bg {
      background-image: url(/img/secret.png);
      background-size: 100% 100%;
      background-position: center;
      background-repeat: no-repeat;
      opacity: 0;
      top: -300px;
      left: 0;
      bottom: 0;
      right: 0;
      position: absolute;
      pointer-events: none;
      z-index: -1;
      transition: opacity .3s ease-in;
    }

    &.active-secret {
      > section {
        background-color: transparent!important;
      }
      .text-highlight-line {
        display: none!important;
      }
      .sections-wrapper__bg {
        opacity: 1;
      }
    }
  }

</style>



// WEBPACK FOOTER //
// src/App.vue